import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';

const UserQuestions = () => {
    const { user } = useAuth();
    const { config } = useConfig();
    const [questions, setQuestions] = useState([]);
    const [speakers, setSpeakers] = useState([]);
    const [newQuestion, setNewQuestion] = useState({ speaker_id: '', text: '' });
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState('');

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 2000);
        return () => clearInterval(interval);
    }, []);

    const fetchData = async () => {
        try {
            const [qRes, sRes] = await Promise.all([
                api.get('/questions'),
                api.get('/speakers')
            ]);
            setQuestions(qRes.data);
            setSpeakers(sRes.data);
        } catch (error) {
            console.error("Error fetching data", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newQuestion.speaker_id || !newQuestion.text) return;
        
        setIsSubmitting(true);
        setMessage('');
        try {
            await api.post('/questions', newQuestion);
            setMessage('¡Pregunta enviada con éxito!');
            setNewQuestion({ ...newQuestion, text: '' });
            fetchData();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error("Error submitting question", error);
            setMessage('Error al enviar la pregunta.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto animate-fadeIn pb-20">
            <header className="mb-10 text-center">
                <h2 className="text-4xl font-black text-white italic uppercase tracking-tighter">Preguntas al Expositor</h2>
                <p className="text-white/60 text-sm mt-2 font-medium">Tus inquietudes serán respondidas por nuestro staff médico.</p>
            </header>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                {/* Form Section */}
                <div className="bg-white/10 backdrop-blur-xl p-8 rounded-[40px] border border-white/10 shadow-2xl h-max">
                    <h3 className="text-xl font-bold text-white mb-6 flex items-center">
                        <span className="mr-3">✍️</span> Nueva Pregunta
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-3 ml-2">Seleccionar Expositor</label>
                            <select 
                                className="w-full bg-white/5 border border-white/10 p-4 rounded-2xl text-white text-sm focus:bg-white/10 transition-all outline-none"
                                value={newQuestion.speaker_id}
                                onChange={e => setNewQuestion({...newQuestion, speaker_id: e.target.value})}
                                required
                            >
                                <option value="" className="bg-slate-900">Elegir conferencista...</option>
                                {speakers.map(s => (
                                    <option key={s.id} value={s.id} className="bg-slate-900">{s.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-white/40 uppercase tracking-[0.2em] mb-3 ml-2">Tu Pregunta</label>
                            <textarea 
                                className="w-full bg-white/5 border border-white/10 p-5 rounded-3xl text-white text-sm focus:bg-white/10 transition-all outline-none min-h-[120px]"
                                placeholder="Escribe tu duda aquí..."
                                value={newQuestion.text}
                                onChange={e => setNewQuestion({...newQuestion, text: e.target.value})}
                                required
                            />
                        </div>
                        <button 
                            type="submit"
                            disabled={isSubmitting}
                            className="w-full bg-purple-600 text-white py-4 rounded-3xl font-black uppercase tracking-widest text-[11px] shadow-xl hover:bg-purple-500 transition-all active:scale-95 disabled:opacity-50"
                        >
                            {isSubmitting ? 'Enviando...' : 'Enviar Pregunta'}
                        </button>
                        {message && (
                            <div className={`text-center text-xs font-bold p-3 rounded-2xl ${message.includes('éxito') ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                                {message}
                            </div>
                        )}
                    </form>
                </div>

                {/* History Section */}
                <div className="space-y-6">
                    <h3 className="text-xl font-bold text-white mb-2 flex items-center ml-4">
                        <span className="mr-3">📜</span> Mi Historial
                    </h3>
                    
                    {isLoading ? (
                        <div className="text-center py-10 text-white/20">Cargando tus preguntas...</div>
                    ) : (
                        <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                            {questions.map(q => (
                                <div key={q.id} className="bg-white/5 border border-white/5 p-6 rounded-[35px] hover:bg-white/10 transition-all">
                                    <div className="flex justify-between items-start mb-3">
                                        <span className="text-[9px] font-black text-white/30 uppercase tracking-widest">Para: {q.speaker_name}</span>
                                        <span className="text-[8px] text-white/20 font-medium">{new Date(q.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <p className="text-white font-medium text-sm leading-relaxed mb-4">"{q.text}"</p>
                                    
                                    {q.reply ? (
                                        <div className="bg-purple-600/20 border border-purple-600/30 p-4 rounded-2xl relative mt-2 animate-fadeIn">
                                            <span className="absolute -top-2.5 left-4 bg-purple-600 text-white px-2 py-0.5 rounded-full text-[7px] font-black uppercase">Respuesta del Staff</span>
                                            <p className="text-purple-100 text-xs italic leading-relaxed">{q.reply}</p>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-2 text-white/20 text-[9px] font-black uppercase tracking-widest mt-2 pl-2">
                                            <div className="w-1.5 h-1.5 rounded-full bg-white/10 animate-pulse"></div>
                                            Pendiente de respuesta
                                        </div>
                                    )}
                                </div>
                            ))}
                            {questions.length === 0 && (
                                <div className="text-center py-20 bg-white/5 rounded-[40px] border border-dashed border-white/10">
                                    <p className="text-white/20 font-bold text-xs">No has realizado preguntas aún.</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UserQuestions;
